import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import useTranslations from "@/shared/lang/useTranslations";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";
import { formatRail } from "@/shared/utils/railDisplay";

import { LIQUIDITY_STATUS_LABEL_KEYS } from "../../constants/payoutBatches.constants";
import type { ProviderBalanceView } from "../../types/payoutBatches.types";
import { balanceVerdict, isProviderBalanceAtRisk } from "../../utils/batchSummary";

export interface ProviderLiquidityRowProps {
  balance: ProviderBalanceView | null;
  country: string;
  rail: string;
  /** Gross the beneficiaries receive, in the delivery currency — what the float has to cover. */
  receiveGross: string;
}

/**
 * INTERNAL only: whether the corridor's float covers the batch. Labelled by corridor ("COL · Bre-B"); the
 * provider behind it is never named on screen (rule 12).
 */
export function ProviderLiquidityRow({ balance, country, rail, receiveGross }: ProviderLiquidityRowProps) {
  const { t, activeLanguage } = useTranslations("payoutBatches");
  if (!balance) return null;

  const atRisk = isProviderBalanceAtRisk(balance, receiveGross);
  const label =
    balance.status === "OK"
      ? balanceVerdict(balance.available, receiveGross) === "sufficient"
        ? t("liquidity_ok")
        : t("liquidity_insufficient")
      : t(LIQUIDITY_STATUS_LABEL_KEYS[balance.status] ?? "liquidity_unavailable");

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs">
      <span className="text-muted-foreground">
        {t("liquidity_title", { corridor: `${country} · ${formatRail(rail)}` })}
      </span>
      <span className="flex items-center gap-2">
        {balance.available !== null && (
          <span className="font-mono text-foreground tabular-nums">
            {t("liquidity_available", {
              amount: `${formatCurrencyAmount(balance.available, balance.currency, activeLanguage)} ${balance.currency}`,
            })}
          </span>
        )}
        <ColocaStatusPill
          size="sm"
          variant={atRisk ? (balance.status === "OK" ? "danger" : "warning") : "success"}
          withDot
        >
          {label}
        </ColocaStatusPill>
      </span>
    </div>
  );
}
